import React, {useEffect, useRef} from 'react';
import {Button, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography} from '@mui/material';
import TableHeader from '../layout/table/TableHeader';
import NoDataLabel from '../layout/NoDataLabel';
import {pavEtBacs} from './atelierRules';
import {formatHeuresDepuisMinutes, formatKm, libelleEquipage} from './simulationRules';

const HEAD_JOURNEES = [
    {id: 'date', label: 'Date'},
    {id: 'equipe', label: 'Équipe'},
    {id: 'equipage', label: 'Équipage'},
    {id: 'trajet', label: 'Trajet'},
    {id: 'bacs', label: 'PAV · bacs'},
    {id: 'heures', label: 'Heures'},
    {id: 'km', label: 'Km'},
    {id: 'bph', label: 'Bac/h'},
    {id: 'signal', label: 'Signal'},
];

/** Pastille de l'indicateur bac/h (vert / rouge, tokens du toggle Oui/Non). */
function Pastille({indicateur}) {
    const vert = indicateur === 'vert';
    return <span aria-label={vert ? 'vert' : 'rouge'}
                 style={{display: 'inline-block', width: 10, height: 10, borderRadius: '50%', marginRight: 6,
                     backgroundColor: vert ? 'seagreen' : 'firebrick'}}/>;
}

/** Ligne dépliée : s'amène à l'écran à l'ouverture (« Voir le détail » depuis la carte). */
function LigneDetail({colSpan, children}) {
    const ref = useRef(null);
    useEffect(() => { ref.current?.scrollIntoView?.({block: 'nearest'}); }, []);
    return <TableRow ref={ref} data-testid="detail-journee-contenu"><TableCell colSpan={colSpan} sx={{p: 1}}>{children}</TableCell></TableRow>;
}

/**
 * Table des journées d'un scénario (journée, équipage, PAV / bacs, heures, km, bac/h, signal, raison d'équipage).
 * Partagée par la page scénario et l'onglet Journées de l'atelier. `onSelect(journee)` au clic sur une ligne ;
 * `estSelectionnee(journee)` surligne la ligne. Lot 9 : `onDetail(journee)` ajoute un bouton « Détail » par ligne (sans déclencher
 * `onSelect`), `detailOuvert(journee)` dit quelle ligne est dépliée et `renderDetail(journee)` fournit son contenu sous la ligne.
 */
function TableJournees({simulation, onSelect, estSelectionnee = () => false, onDetail, detailOuvert = () => false, renderDetail}) {
    const journees = simulation.journees ?? [];
    const tete = onDetail ? [...HEAD_JOURNEES, {id: 'detail', label: ''}] : HEAD_JOURNEES;
    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small" aria-label="journées du scénario">
                <TableHeader headCells={tete} noAction/>
                <TableBody>
                    {journees.length === 0 &&
                        <TableRow><TableCell colSpan={tete.length} align="center"><NoDataLabel label="Aucune journée dans ce scénario."/></TableCell></TableRow>}
                    {journees.map((j) => (
                        <React.Fragment key={`${j.date}-${j.equipe}`}>
                        <TableRow hover selected={estSelectionnee(j)}
                                  style={{cursor: 'pointer'}} onClick={() => onSelect?.(j)} data-testid="ligne-journee">
                            <TableCell>{j.date}</TableCell>
                            <TableCell>{j.equipe}</TableCell>
                            <TableCell>
                                {libelleEquipage(j.equipage)}
                                {j.raisonEquipage && <Typography variant="caption" sx={{display: 'block', color: 'var(--thm-gray)'}}>{j.raisonEquipage}</Typography>}
                            </TableCell>
                            <TableCell>{j.libelleTrajet ?? '—'}</TableCell>
                            <TableCell>{pavEtBacs(j.pav, j.bacs)}</TableCell>
                            <TableCell>{formatHeuresDepuisMinutes(j.minutesTravail)}</TableCell>
                            <TableCell>{formatKm(j.km)}</TableCell>
                            <TableCell><Pastille indicateur={j.indicateur}/>{j.bacsParHeure}</TableCell>
                            <TableCell>{j.signal ?? ''}</TableCell>
                            {onDetail &&
                                <TableCell>
                                    <Button size="small" data-testid="detail-journee" aria-expanded={detailOuvert(j)}
                                            onClick={(e) => { e.stopPropagation(); onDetail(j); }}
                                            sx={{textTransform: 'none', fontWeight: 700}}>Détail</Button>
                                </TableCell>}
                        </TableRow>
                        {onDetail && detailOuvert(j) &&
                            <LigneDetail colSpan={tete.length}>{renderDetail?.(j)}</LigneDetail>}
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default TableJournees;
